<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import { toast } from "@/common/utils/toast";

import { memberGet, memberPatch } from "@/common/api/rest/member";

import type { MemberFullType, MemberPayloadType } from "@/common/types/member";

import { PanelTheme } from "@/common/themes";
import {
  baseAvatar,
  baseInput,
  baseSelect,
  breadcrumbSimple,
  tabMenu,
} from "@/common/components";

type TabActiveType = "edit" | "cv";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const member = ref<MemberFullType | null>(null);
const memberFullName = ref<string>("");

const memberId = computed(() => route.params.member as string);

const breadcrumbList = computed(() => [
  {
    key: "members",
    label: t("common.labels.members"),
    url: "/members",
  },
  {
    key: "member",
    label: `${member.value?.profile?.first_name ?? ""} ${member.value?.profile?.last_name ?? ""}`,
  },
  {
    key: "edit",
    label: t("common.labels.edit"),
  },
]);

const tabList = computed(() => [
  {
    key: "edit",
    label: t("common.labels.member").toUpperCase(),
  },
  {
    key: "cv",
    label: t("common.labels.cv").toUpperCase(),
  },
]);

const activeOptions = computed(() => [
  { option: t("common.labels.active"), value: true },
  { option: t("common.labels.inactive"), value: false },
]);

function fetchData() {
  memberGet(memberId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        member.value = data;
        memberFullName.value = `${data.profile.first_name} ${data.profile.last_name}`;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateMember(key: keyof MemberPayloadType) {
  const payload = {
    [key]: member.value[key],
  };

  if (key === "full_name") {
    payload[key] = memberFullName.value;
  }

  memberPatch(memberId.value, payload as any)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function selectTab(tab: TabActiveType) {
  router.push(`/members/${memberId.value}/${tab}`);
}

onMounted(() => {
  fetchData();
});
</script>

<template>
	<panel-theme>
		<article class="member-page">
			<section class="page-header">
				<breadcrumb-simple
					:items="breadcrumbList"
					:show-home="true"
					url-home="/home"
					active="edit"
				/>
			</section>

			<section class="page-content">
				<tab-menu
					:tabs="tabList"
					:tooltip="false"
					active="edit"
					width="fit-content"
					padding-inline="1.5rem"
					@select:tab="selectTab"
				>
					<template #content>
						<template v-if="member">
							<div class="flex justify-center">
								<base-avatar
									position="center"
									:is-edit="false"
									:show-image="true"
									:url="member?.avatar?.large"
								/>
							</div>

							<div class="member-form">
								<base-input
									v-model="memberFullName"
									:value="memberFullName"
									:placeholder="t('common.placeholders.name')"
									:required="true"
									:max-length="75"
									@blur="updateMember('full_name')"
								>
									<template #label> {{ t("common.labels.name") }} * </template>
								</base-input>

								<base-select
									v-model="member.active"
									:value="member.active"
									:options="activeOptions"
									:is-empty="false"
									@change="updateMember('active')"
								>
									<template #label> {{ t("common.labels.active") }}</template>
								</base-select>

								<base-input
									v-model="member.login"
									:value="member.login"
									:placeholder="t('common.placeholders.login')"
									:required="true"
									:max-length="75"
									@blur="updateMember('login')"
								>
									<template #label> {{ t("common.labels.login") }} * </template>
								</base-input>

								<base-input
									v-model="member.email"
									:value="member.email"
									:placeholder="t('common.placeholders.email')"
									:required="true"
									:max-length="50"
									@blur="updateMember('email')"
								>
									<template #label>
										{{ t("common.labels.email") }}
									</template>
								</base-input>
							</div>
						</template>
					</template>
				</tab-menu>
			</section>
		</article>
	</panel-theme>
</template>

<style lang="scss" scoped>
.member-page {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-inline: 1rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    margin-block-end: 1.54rem;

    .member-form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }
}
</style>
